import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../../../utils/apiConfig';
import { useAuth } from '../../../contexts/AuthContext';
import './StaffTable.css';

const StaffTable = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, userProfile } = useAuth();

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.warn('No authentication token found');
          setStaffList([]);
          return;
        }
        
        // Use the staff endpoint that doesn't require admin privileges
        const response = await axios.get(`${API_CONFIG.BASE_URL}/api/staff/active/minimal`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        // The staff endpoint returns active staff with minimal data
        const staffData = response.data?.staff || response.data || [];
        const staffMembers = Array.isArray(staffData) ? staffData.map(staff => ({
          id: staff._id || staff.id,
          name: staff.name || 'Unknown',
          email: staff.email || 'N/A',
          role: staff.role || 'staff',
          department: staff.department || 'N/A',
          joinDate: staff.createdAt ? new Date(staff.createdAt).toLocaleDateString() : 'N/A',
          lastLogin: staff.lastLoginAt ? new Date(staff.lastLoginAt).toLocaleDateString() : 'Never',
          status: 'Active' // All staff from this endpoint are active
        })) : [];
        
        console.log('Staff API response:', response);
        console.log('Filtered staff members:', staffMembers);
        setStaffList(staffMembers);
      } catch (err) {
        console.error('Error fetching staff:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        
        // Show a more user-friendly error message
        if (err.response?.status === 403) {
          console.warn('Access denied - user may not have required permissions');
        } else if (err.response?.status === 404) {
          console.warn('Staff endpoint not found - using offline data');
        }
        
        // Set fallback data when API is unavailable
        setStaffList([
          { 
            name: 'John Doe', 
            email: 'john@example.com', 
            role: 'Staff', 
            department: 'General', 
            isActive: true,
            status: 'active',
            joinDate: '2024-01-15',
            lastLogin: '2024-01-20'
          },
          { 
            name: 'Jane Smith', 
            email: 'jane@example.com', 
            role: 'Manager', 
            department: 'Sales', 
            isActive: true,
            status: 'active',
            joinDate: '2024-01-10',
            lastLogin: '2024-01-20'
          },
          { 
            name: 'Mike Johnson', 
            email: 'mike@example.com', 
            role: 'Staff', 
            department: 'Support', 
            isActive: true,
            status: 'active',
            joinDate: '2024-01-12',
            lastLogin: '2024-01-19'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
    
    // Set up real-time updates every 30 seconds
    const interval = setInterval(fetchStaff, 30000);
    
    return () => clearInterval(interval);
  }, [currentUser]); // Re-fetch when current user changes

  // Get current user info
  const currentUserInfo = currentUser ? {
    name: userProfile?.name || currentUser.displayName || 'Current User',
    email: currentUser.email || 'N/A',
    role: userProfile?.role || 'Staff',
    department: userProfile?.department || 'General',
    status: 'Active (You)',
    joinDate: userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'N/A',
    lastLogin: 'Now',
    isCurrentUser: true
  } : null;

  // Update staff list to include current user with real-time status
  const updatedStaffList = staffList.map(staff => {
    // If this staff member is the current user, update their status
    if (currentUser && (staff.email === currentUser.email || staff.id === currentUser.uid)) {
      return {
        ...staff,
        status: 'Active (Online)',
        lastLogin: 'Now',
        isCurrentUser: true
      };
    }
    return staff;
  });

  // If current user is not in the staff list, add them
  const finalStaffList = currentUser && !updatedStaffList.some(staff => staff.isCurrentUser) 
    ? [currentUserInfo, ...updatedStaffList]
    : updatedStaffList;

  return (
    <div className="staff-table">
      <h3>Staff Members</h3>
      
      {/* Current User Section */}
      {currentUserInfo && (
        <div className="current-user-section" style={{ 
          background: '#e8f5e8', 
          padding: '15px', 
          marginBottom: '20px', 
          borderRadius: '8px',
          border: '2px solid #4caf50'
        }}>
          <h4 style={{ color: '#2e7d32', margin: '0 0 10px 0' }}>👤 Currently Logged In</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div><strong>Name:</strong> {currentUserInfo.name}</div>
            <div><strong>Email:</strong> {currentUserInfo.email}</div>
            <div><strong>Role:</strong> {currentUserInfo.role}</div>
            <div><strong>Department:</strong> {currentUserInfo.department}</div>
            <div><strong>Status:</strong> 
              <span className={`status-badge active`} style={{ marginLeft: '5px' }}>
                {currentUserInfo.status}
              </span>
            </div>
            <div><strong>Join Date:</strong> {currentUserInfo.joinDate}</div>
            <div><strong>Last Login:</strong> {currentUserInfo.lastLogin}</div>
          </div>
        </div>
      )}

      {/* Other Staff Members */}
      <h4>All Staff Members</h4>
      {loading ? (
        <div className="loading">Loading staff members...</div>
      ) : (
        <table className="staff-table-card" aria-label="Staff Members Table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Status</th>
              <th scope="col">Join Date</th>
              <th scope="col">Last Login</th>
            </tr>
          </thead>
          <tbody>
            {finalStaffList.length > 0 ? (
              finalStaffList.map((staff, index) => (
                <tr key={staff.id || index} style={staff.isCurrentUser ? { backgroundColor: '#f0f8f0', fontWeight: 'bold' } : {}}>
                  <td data-label="Name">
                    {staff.name || 'N/A'}
                    {staff.isCurrentUser && <span style={{ color: '#4caf50', marginLeft: '5px' }}>👤</span>}
                  </td>
                  <td data-label="Email">{staff.email || 'N/A'}</td>
                  <td data-label="Status">
                    <span className={`status-badge ${(staff.status || 'active').toLowerCase()}`}>
                      {staff.status || 'Active'}
                    </span>
                  </td>
                  <td data-label="Join Date">{staff.joinDate}</td>
                  <td data-label="Last Login">{staff.lastLogin}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  No staff members found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default StaffTable;